import { isDefined, isNull, isObject } from '@aloft/toolkit-shared';
import { Middleware, PayloadAction } from '@reduxjs/toolkit';

import { get, set } from 'lodash';

import { RootState } from './types.ts';

// To be honest, I'm not 100% I'm using the types from redux toolkit correctly;
// there may be a better way to do this. But I spent a while fighting typescript
// and at least at this point nothing is too mad.
function isPayloadAction(action: unknown): action is PayloadAction {
  return (action as PayloadAction).payload !== undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasToRedux(obj: any): boolean {
  return isDefined(obj) && !isNull(obj) && typeof obj.toRedux === 'function';
}

// getAloftObjectPaths recursively searches for all paths in an object that have
// a toRedux method and returns an array of those paths. For example, if we
// passed in an object that looks like this (assuming that AloftTime has a
// toRedux method):
// {
//   createTime: AloftTime,
//   id: string,
//   extraStuff: {
//     extraData: string,
//     extraTime: AloftTime,
//   },
// }
// We would get back an array that looks like this:
// ['createTime', 'extraStuff.extraTime']
// which are usable with `get` and `set`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getAloftObjectPaths(obj: any, path?: string): string[] {
  const prefix = path ? `${path}.` : '';

  const toReduxKeys = Object.keys(obj)
    .filter((key) => hasToRedux(obj[key]))
    .map((key) => `${prefix}${key}`);

  const nestedReduxKeys = Object.keys(obj)
    //  we assume that if an object has a toRedux method, it isn't going to have
    //  children that we need to examine
    .filter((key) => isObject(obj[key]) && !hasToRedux(obj[key]))
    .flatMap((key) => getAloftObjectPaths(obj[key], `${prefix}${key}`));

  const arrayReduxKeys = Object.keys(obj)
    .filter((key) => Array.isArray(obj[key]))
    .flatMap((key) => (obj[key] as any[]).flatMap((item, index) => getAloftObjectPaths(item, `${prefix}${key}[${index}]`)));

  return toReduxKeys.concat(nestedReduxKeys).concat(arrayReduxKeys);
}

// eslint-disable-next-line @typescript-eslint/ban-types -- see https://redux.js.org/usage/usage-with-typescript#type-checking-middleware
export const serializeAloftObjectMiddleware: Middleware<{}, RootState> =
  () => (next) => (action) => {
    if (!isPayloadAction(action) || !isObject(action.payload)) {
      return next(action);
    }

    const aloftObjectPaths = getAloftObjectPaths(action.payload);

    // for any value in the payload that has a toRedux method, replace that
    // value with the result of toRedux()
    if (aloftObjectPaths.length > 0 && isObject(action.payload)) {
      action.payload = Object.assign({}, action.payload);
      aloftObjectPaths.forEach((path) => {
        set(action.payload as unknown as object, path, get(action.payload, path).toRedux());
      });
    }

    return next(action);
  };
